import React, { useCallback } from 'react';
import { useLingui } from '@lingui/react/macro';

import {
  STAR_SITTER_BADGE_PROPS,
  STAR_SITTER_BADGE_TEXT,
} from '@rover/components/src/callouts/CalloutBadge/CalloutBadge.constants';
import CalendarRecency from '@rover/components/src/cards/SearchResultCard/CalendarRecency';
import ImageColumn from '@rover/components/src/cards/SearchResultCard/ImageColumn';
import InfoColumn from '@rover/components/src/cards/SearchResultCard/InfoColumn';
import PillsAndFavoriteRow from '@rover/components/src/cards/SearchResultCard/PillsAndFavoriteRow';
import { MemberProfileAnchor } from '@rover/components/src/cards/SearchResultCard/SearchResultCard';
import { SEARCH_RESULT_CARD_Z_INDEX } from '@rover/components/src/cards/SearchResultCard/SearchResultCard.constants';
import AddToShortlistButton from '@rover/components/src/shortlist/components/AddToShortlistButton';
import useShortlist from '@rover/components/src/shortlist/hooks/useShortlist';
import { Box, Button, Flex, Link } from '@rover/kibble/core';
import { Badge } from '@rover/kibble/official-patterns/Badge';
import Divider from '@rover/kibble/patterns/Divider';
import { HorizontalLayout } from '@rover/react-lib/src/components/layouts';
import useOnIntersect from '@rover/react-lib/src/hooks/useOnIntersect';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import type { SearchResult } from '@rover/types/src/apiResponses/FetchSearchResponse';
import { SearchCardTreatment } from '@rover/types/src/SearchCardTreatment';

import { type SearchNameCardClickTarget, SearchNameScrollDepthEvent } from '../analyticsEvents';
import type { NameSearchResult } from '../types';

type Props = {
  result: NameSearchResult;
  rank: number;
  resultCount: number;
  searchUuid: string | null;
  onCardClick?: (target: SearchNameCardClickTarget) => void;
};

const SearchByNameSitterCard = ({
  result,
  rank,
  resultCount,
  searchUuid,
  onCardClick,
}: Props): JSX.Element => {
  const { t, i18n } = useLingui();
  const {
    personOpk,
    shortName,
    avatarUrl,
    profileUrl,
    isPreviouslyBooked,
    isStarSitter,
    primaryBadgeData,
    ratingsAverage,
    reviewsCount,
    repeatClientCount,
    yearsOfExperience,
    calendarRecency,
  } = result;

  const handleClick = useCallback(
    (target: SearchNameCardClickTarget): void => onCardClick?.(target),
    [onCardClick]
  );

  const handleScrollDepth = useCallback(() => {
    emitAnalyticsEvent(
      new SearchNameScrollDepthEvent({
        rank,
        sitterPersonOpk: personOpk,
        resultCount,
        searchUuid,
        isPreviouslyBooked,
        isStarSitter,
      })
    );
  }, [rank, personOpk, resultCount, searchUuid, isPreviouslyBooked, isStarSitter]);

  const sectionRef = useOnIntersect(handleScrollDepth, true, { threshold: 0.75 });

  const { isVisible: isShortlistVisible } = useShortlist();
  const contactText = t`Contact`;
  const contactAriaLabel = t`Contact ${shortName}`;

  return (
    <Box ref={sectionRef} position="relative" testID={`search-by-name-sitter-card-${personOpk}`}>
      <MemberProfileAnchor
        href={profileUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => handleClick('profile')}
        aria-label={shortName}
        data-testid={`search-by-name-card-anchor-${personOpk}`}
      />
      <HorizontalLayout noWrap data-testid="legacy-card-content">
        <ImageColumn
          displayExpanded={false}
          desktopProfileImage={avatarUrl?.medium ?? ''}
          mobileProfileImage={avatarUrl?.small ?? ''}
          shortName={shortName}
          lazy
          badge={primaryBadgeData ?? undefined}
          cardTreatment={SearchCardTreatment.legacy}
        />
        <InfoColumn
          displayExpanded={false}
          searchResult={
            {
              ...result,
              // we specifically wanted to suppress legacy badges here
              secondaryBadgeData: [],
              // Rendered through nameAccessory instead, so it aligns with the
              // reduced-size name heading this card always uses.
              profileStatusBadgeData: null,
              distanceFromSearchCenterObj: { distanceDisplay: result.distanceDisplay },
            } as unknown as SearchResult
          }
          isSeoPage={false}
          hideHeadline
          nameAccessory={
            <>
              {isStarSitter && (
                <Badge {...STAR_SITTER_BADGE_PROPS} text={i18n._(STAR_SITTER_BADGE_TEXT)} />
              )}
              {isPreviouslyBooked && <Badge variant="info" text={t`Previously booked`} />}
            </>
          }
        />
      </HorizontalLayout>
      <PillsAndFavoriteRow
        ratingsAverage={Number.isFinite(ratingsAverage) ? String(ratingsAverage) : ''}
        reviewsCount={reviewsCount ?? 0}
        repeatClientCount={repeatClientCount ?? 0}
        yearsOfExperience={yearsOfExperience ?? ''}
        isAuthenticated={false}
        isFavorite={false}
        onFavoriteToggled={() => {}}
        hideFavorite
      />
      {calendarRecency && (
        <Flex justifyContent="space-between" width="100%" flexWrap="wrap" gap="3x">
          <CalendarRecency calendarRecency={calendarRecency} />
        </Flex>
      )}
      <Flex
        justifyContent="flex-start"
        alignItems="center"
        gap="4x"
        my="2x"
        position="relative"
        zIndex={`${SEARCH_RESULT_CARD_Z_INDEX.INTERACTIVE}`}
      >
        <Link
          href={profileUrl}
          onPress={() => handleClick('profile')}
          variant="primary"
          size="200"
          underline={false}
          target="_blank"
          rel="noopener noreferrer"
          webProps={{ 'aria-label': t`View Profile, ${shortName}` }}
          testID={`search-by-name-view-profile-${personOpk}`}
        >
          {t`View Profile`}
        </Link>
        {isShortlistVisible ? (
          <AddToShortlistButton
            personOpk={personOpk}
            source="search-by-name"
            size="small"
            removedText={contactText}
            removedIcon={null}
            removedVariant="primary"
            onAdded={() => handleClick('intro-message')}
            webProps={{ 'aria-label': contactAriaLabel }}
            testID={`search-by-name-send-message-${personOpk}`}
          />
        ) : (
          <Button
            variant="primary"
            size="small"
            href={`${profileUrl}contact/`}
            onPress={() => handleClick('intro-message')}
            webProps={{
              hrefTarget: '_blank',
              rel: 'noopener noreferrer',
              'aria-label': contactAriaLabel,
            }}
            testID={`search-by-name-send-message-${personOpk}`}
          >
            {contactText}
          </Button>
        )}
      </Flex>
      <Divider />
    </Box>
  );
};

export default SearchByNameSitterCard;
